import { useRef, useState } from 'react';
import classNames from 'classnames';

import { useTrackEvent } from '@/lib/analytics';
import { CellDragKey, getExplorationCellIndex } from '@/core/exploration';
import { canDropOnRow, isCellDragEvent, trackDropEvent } from '@/core/exploration';
import { Cell } from '@/explore/types';
import { getCell } from '@/core/cell';

import { useExplorationContext } from '../exploration-context';

import styles from './exploration-layout.module.scss';

interface CellDropZoneProps {
  cell: Cell;
  mergeIntoRow?: boolean;
  addBefore?: boolean;
  vertical?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const CellDropZone = (props: CellDropZoneProps) => {
  const { cell, mergeIntoRow, addBefore } = props;
  const [isDragOver, setIsDragOver] = useState(false);
  const element = useRef<HTMLDivElement>(null);

  const { exploration, moveCell } = useExplorationContext();
  const trackEvent = useTrackEvent();

  return (
    <div
      className={classNames(styles.cellDropZone, props.className, {
        [styles.isDragOver]: isDragOver,
        [styles.vertical]: props.vertical,
      })}
      ref={element}
      onDragEnd={() => {
        setIsDragOver(false);
      }}
      onDragOver={(event) => {
        if (
          !isCellDragEvent(event) ||
          (mergeIntoRow === true && !canDropOnRow(exploration, cell, event))
        ) {
          event.dataTransfer.dropEffect = 'none';
          return;
        }
        event.dataTransfer.dropEffect = 'move';
        event.preventDefault();
      }}
      onDragEnter={(event) => {
        if (
          !isCellDragEvent(event) ||
          (mergeIntoRow === true && !canDropOnRow(exploration, cell, event))
        ) {
          return;
        }
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={(event) => {
        if (
          element.current &&
          (!element.current.contains(event.target as Node) || element.current === event.target)
        ) {
          setIsDragOver(false);
        }
      }}
      onDrop={(event) => {
        if (
          !isCellDragEvent(event) ||
          (mergeIntoRow === true && !canDropOnRow(exploration, cell, event))
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        const droppedCellId = event.dataTransfer.getData(CellDragKey);
        const droppedCellIndex = getExplorationCellIndex(exploration, droppedCellId);
        if ((droppedCellId === cell.id && Boolean(mergeIntoRow)) || droppedCellIndex === -1) {
          return;
        }
        moveCell(droppedCellId, cell.id, addBefore !== true, mergeIntoRow);
        trackDropEvent(
          exploration,
          droppedCellIndex,
          getExplorationCellIndex(exploration, cell.id),
          getCell(exploration.view.cells, droppedCellId),
          addBefore !== true,
          mergeIntoRow === true,
          trackEvent,
        );
      }}>
      {props.children}
    </div>
  );
};
